import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from '../../redux/redux-store';
import { useGetMediaPlanQuery } from "src/redux/survey/media-plan.endpoints";
import { SurveyMediaPlan } from "src/redux/survey/media-plan.types";
import { useGetSurveyQuery, useGetSurveyStatsQuery, useGetSurveySummaryQuery } from "src/redux/survey/survey.endpoints";
import { SurveyFull, SurveyStats, SurveySummaryData } from "src/redux/survey/survey.types";
import { clearSelectedAnswers } from "src/redux/survey/survey-selected-answers.slice";

export interface ViewSurveyChildrenProps {
  surveyId: string;
  isShare: boolean;
  isLoadingSummary?: boolean;
  isGeneratingSummary?: boolean;
  isLoadingMediaPlan?: boolean;
  isGeneratingMediaPlan?: boolean;
  createSummary?: () => void;
  createMediaPlan?: () => void;
}

interface UseViewSurveyChildrenReturn {
  readonly isSurveyError: boolean;
  readonly survey?: SurveyFull;
  readonly stats?: SurveyStats;
  readonly summary?: SurveySummaryData;
  readonly mediaPlan?: SurveyMediaPlan;
}

export type ViewSurveyChildrenOptions = {
  /** When set (e.g. survey group results), skip stats fetch and use this payload. */
  statsOverride?: SurveyStats;
  summaryOverride?: SurveySummaryData;
  /** Skip GET stats when each question carries its own stats (e.g. merged survey group list). */
  skipStatsFetch?: boolean;
  skipSummaryFetch?: boolean;
  /** Skip GET survey (and media plan) when results come only from group payload. */
  skipSurveyFetch?: boolean;
};

export const useViewSurveyChildren = (
  surveyId: string,
  isShare: boolean = false,
  options?: ViewSurveyChildrenOptions
): UseViewSurveyChildrenReturn => {
  const dispatch = useDispatch();
  const selectedAnswerIds = useSelector((state: RootState) => {
    return state.selectedAnswers.selectedAnswerIds;
  });

  // Clear selected answers when survey changes
  useEffect(() => {
    if (surveyId) {
      dispatch(clearSelectedAnswers());
    }
  }, [surveyId, dispatch]);

  const params = { ext_survey_id: surveyId, share: isShare, selected_survey_answers: selectedAnswerIds };
  const skipSurvey = Boolean(options?.skipSurveyFetch);
  const { data: survey, isError: isSurveyError } = useGetSurveyQuery(params, { skip: !surveyId || skipSurvey });
  const skipStats = Boolean(options?.statsOverride) || Boolean(options?.skipStatsFetch);
  const skipSummary = Boolean(options?.summaryOverride) || Boolean(options?.skipSummaryFetch);
  const { data: statsFromApi } = useGetSurveyStatsQuery(params, { skip: !surveyId || skipStats });
  const { data: summaryFromApi } = useGetSurveySummaryQuery(params, { skip: !surveyId || skipSummary });
  const { data: mediaPlan } = useGetMediaPlanQuery(params, { skip: !surveyId || skipSurvey });

  const stats = options?.statsOverride ?? statsFromApi;
  const summary = options?.summaryOverride ?? summaryFromApi;

  return {
    isSurveyError,
    survey,
    stats,
    summary,
    mediaPlan,
  };
};
